import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Search, ArrowUpRight, Loader2, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const modKeyOf = (route) => {
  const m = /^\/os\/mod\/([a-z0-9_]+)$/.exec(route || "");
  return m ? m[1] : null;
};

// Cross-suite interoperability (Fase D): launch this entity into another suite's
// app, creating a seeded + bidirectionally-linked record there (no data dup).
export default function CrossSuiteTool({ moduleKey, item, onRefresh }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (open && !catalog) api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => setCatalog({ suites: [] }));
  }, [open, catalog]);

  const groups = useMemo(() => {
    if (!catalog) return [];
    const term = q.trim().toLowerCase();
    return (catalog.suites || []).map((s) => {
      const apps = [];
      (s.studios || []).forEach((st) => (st.apps || []).forEach((a) => {
        const k = modKeyOf(a.route);
        if (!k || k === moduleKey) return;
        if (term && !a.name.toLowerCase().includes(term) && !s.name.toLowerCase().includes(term)) return;
        apps.push({ key: k, name: a.name, studio: st.name });
      }));
      return { id: s.id, name: s.name, apps };
    }).filter((g) => g.apps.length);
  }, [catalog, q, moduleKey]);

  const sendToApp = async (app) => {
    setBusy(app.key);
    try {
      const { data } = await api.post(`/mod/${moduleKey}/items/${item.id}/use-in`, { target_module: app.key });
      toast.success(`Enviado a ${app.name}`, {
        action: { label: "Abrir", onClick: () => navigate(`/os/mod/${app.key}?open=${data.item.id}`) },
      });
      setOpen(false); setQ("");
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-3" data-testid="cross-suite-tool">
      <button onClick={() => setOpen(true)} data-testid="cross-suite-open"
        className="inline-flex items-center gap-2 text-sm rounded-full border border-violet/30 text-violet px-4 py-2 hover:bg-violet/5 transition-colors">
        <Sparkles className="w-4 h-4" /> Usar en otra suite
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="cross-suite-modal">
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg bg-cream rounded-2xl shadow-xl overflow-hidden max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy/5">
              <div className="flex items-center gap-2.5">
                <Sparkles className="w-5 h-5 text-violet" />
                <div>
                  <h3 className="font-medium leading-tight">Usar en otra suite</h3>
                  <p className="text-[11px] text-navy/45">Se crea un registro vinculado con el contexto de este ítem. Sin duplicar datos.</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="cross-suite-close" className="text-navy/40 hover:text-navy transition-colors"><X className="w-5 h-5" /></button>
            </div>

            <div className="px-6 py-3 border-b border-navy/5">
              <div className="relative">
                <Search className="w-4 h-4 text-navy/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus data-testid="cross-suite-search"
                  placeholder="Busca una app o suite (ej. Legal, Contratos)…"
                  className="w-full bg-white border border-navy/10 rounded-lg pl-10 pr-3 py-2 text-sm outline-none focus:border-violet" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto thin-scroll px-6 py-4 space-y-5">
              {!catalog ? (
                <div className="flex items-center justify-center py-10 text-navy/40"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : groups.length === 0 ? (
                <div className="text-sm text-navy/40 py-8 text-center">Sin resultados.</div>
              ) : groups.map((g) => (
                <div key={g.id} data-testid={`cross-suite-group-${g.id}`}>
                  <div className="flex items-center gap-1.5 overline text-navy/40 mb-2"><Layers className="w-3 h-3" /> {g.name}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {g.apps.map((app) => (
                      <button key={app.key} onClick={() => sendToApp(app)} disabled={busy} data-testid={`cross-suite-app-${app.key}`}
                        className="group flex items-center justify-between gap-2 bg-white border border-navy/10 rounded-lg px-3 py-2.5 text-left hover:border-violet/50 transition-colors disabled:opacity-50">
                        <span className="min-w-0">
                          <span className="block text-sm font-medium truncate group-hover:text-violet">{app.name}</span>
                          <span className="block text-[11px] text-navy/40 truncate">{app.studio}</span>
                        </span>
                        {busy === app.key ? <Loader2 className="w-4 h-4 animate-spin text-violet shrink-0" /> : <ArrowUpRight className="w-4 h-4 text-navy/30 group-hover:text-violet shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
